import type { Location } from "./types";

/**
 * ACREAGE & FOOTHILLS LOCATIONS — the remaining pages already live, plus the
 * estate corridors west of Calgary.
 *
 * These share one commercial truth: the land is the asset. A five-acre parcel
 * photographed from the driveway shows a house and a hedge. The same parcel
 * from 90 metres shows where the property line actually runs, how the approach
 * reads, and what the buyer is paying for. That is why the aerial case is
 * strongest here and weakest on a zero-lot-line infill.
 */
export const ACREAGE_LOCATIONS: Location[] = [
  {
    slug: "springbank",
    name: "Springbank",
    region: "springbank-elbow",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.08, lng: -114.37 },
    tier: 1,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class C control zone (CYBW)",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 3,
      authorizationRequired: true,
      authorizationNote:
        "Springbank sits inside or immediately adjacent to the Springbank Airport control zone. CYBW is one of Canada's busier training fields, so authorisation here is routine work rather than an exception — and it is filed before every shoot.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["Springbank Airport (CYBW) circuit and approach"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Springbank Airport runs a high volume of circuit training, which means the airspace directly above these acreages is genuinely active most fine-weather days.",
      consequence:
        "This is the single most common place a Basic-certificate operator has to cancel on the morning of a shoot, because they cannot legally be there at all.",
      resolution:
        "Advanced RPAS certification plus authorisation filed at booking means the Springbank date holds for the same reasons any other date holds — weather, not paperwork.",
    },
    landmarks: [
      "Springbank Road",
      "Highway 8",
      "Springbank Links",
      "Harmony",
      "Range Road 33",
    ],
    terrain:
      "Open rolling upland between the Elbow and Bow valleys. Long sightlines west to the front ranges, with shallow coulees breaking the parcels.",
    windNote:
      "Elevated and exposed to the chinook. West wind arrives off the foothills with little to slow it and builds through the afternoon.",
    seasonalWindow:
      "West-facing with an open mountain horizon, so late light is the strongest. Snow lies longer on this upland than in the city below.",
    bylawNote:
      "Rocky View County governs land use here. Launch and landing are arranged on the subject property with the owner's permission.",
    subjectMix: ["acreage-estate", "luxury-residential", "agricultural"],
    mobilisationNote:
      "About 25 minutes west of the Calgary base on Highway 8 or Highway 1. Same-day capable, subject to the CYBW authorisation being in hand.",
    faqs: [
      {
        question: "Do you need authorisation to fly a drone in Springbank?",
        answer:
          "Yes. Springbank sits inside or immediately adjacent to the Springbank Airport (CYBW) Class C control zone. Flights require an Advanced RPAS certificate and a NAV CANADA authorisation, which is filed before the shoot date is confirmed.",
      },
      {
        question: "Why do some drone operators refuse Springbank work?",
        answer:
          "Because a Basic RPAS certificate does not permit operations in controlled airspace, and CYBW is an active training field. Rather than explain that, many operators simply decline the postcode or cancel on the day.",
      },
      {
        question: "What does aerial photography show on a Springbank acreage that ground photography cannot?",
        answer:
          "The parcel itself. From the driveway a five-acre property looks like a house and some trees. From altitude the buyer sees the actual boundary, the approach, the usable land and the mountain sightline they are paying a premium for.",
      },
    ],
    primaryKeywords: [
      "drone photography Springbank",
      "Springbank acreage aerial photography",
      "Springbank estate drone video",
    ],
    nearestLocations: ["elbow-valley", "bearspaw", "aspen-woods", "cochrane", "bragg-creek"],
  },

  {
    slug: "bearspaw",
    name: "Bearspaw",
    region: "rocky-view",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.14, lng: -114.28 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Mixed — CYBW control zone boundary",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Bearspaw straddles the boundary between the Springbank control zone and uncontrolled Class G airspace. Which side a given acreage falls on is not obvious at driveway scale, so it is confirmed against NAV Drone per property.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The Springbank control-zone boundary runs through Bearspaw rather than around it, so two neighbouring acreages can sit under different rules.",
      consequence:
        "An operator who checks the town name instead of the coordinates will get it wrong roughly half the time, and will not find out until they are already on site.",
      resolution:
        "Authorisation status is resolved from the property coordinates at booking, so the answer is settled before anyone drives out.",
    },
    landmarks: [
      "Bearspaw Road",
      "Watermark",
      "Silverhorn",
      "Church Ranches",
      "Highway 1A",
    ],
    terrain:
      "Rolling ridge country north-west of the city, dropping toward the Bow. Long private approaches and mature shelterbelts are the norm.",
    windNote:
      "Exposed ridgelines in the chinook belt. Conditions on a ridge crest can differ markedly from the sheltered hollow a kilometre away.",
    seasonalWindow:
      "Autumn is the strongest season here — mature deciduous shelterbelts turn, and the contrast separates a property from the surrounding grassland.",
    bylawNote:
      "Rocky View County land-use rules apply. Launch is arranged on the subject property with the owner's permission.",
    subjectMix: ["acreage-estate", "luxury-residential"],
    mobilisationNote:
      "About 25 minutes north-west of the Calgary base. Same-day capable once the per-property airspace check is complete.",
    faqs: [
      {
        question: "Is Bearspaw inside controlled airspace?",
        answer:
          "Partly. The Springbank (CYBW) control-zone boundary runs through the Bearspaw area, so some acreages sit inside it and some do not. The status is confirmed from the property coordinates against NAV Drone before the shoot is booked.",
      },
      {
        question: "When is the best season to photograph a Bearspaw acreage?",
        answer:
          "Autumn. The mature shelterbelts that define most Bearspaw properties turn colour, which separates the parcel visually from the surrounding grassland. In midsummer the same property can read as one continuous field of green.",
      },
      {
        question: "How is a long private approach best filmed?",
        answer:
          "As a single moving pass down the approach at low altitude, resolving into a rise over the house. The approach is part of what an estate buyer is purchasing, and a static frame cannot convey its length.",
      },
    ],
    primaryKeywords: [
      "drone photography Bearspaw",
      "Bearspaw acreage drone video",
      "Bearspaw estate aerial photography",
    ],
    nearestLocations: ["springbank", "cochrane", "calgary", "gleneagles", "elbow-valley"],
  },

  {
    slug: "elbow-valley",
    name: "Elbow Valley",
    region: "springbank-elbow",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.01, lng: -114.33 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class C control zone (CYBW) vicinity",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "The Elbow Valley corridor sits within the Springbank control zone's area of influence. Authorisation is filed per site before the shoot is confirmed.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The Elbow Valley corridor is one of the few places in the region where estate housing sits directly against a working river valley and mature forest.",
      consequence:
        "Tree canopy hides most of what a buyer wants to see — the water, the lot depth, and how much of the parcel is actually open ground.",
      resolution:
        "A pass flown just above canopy height reveals the property's true footprint against the valley without losing the sense of enclosure that sells it.",
    },
    landmarks: [
      "Elbow Valley Estates",
      "Highway 8",
      "the Elbow River",
      "Wood Valley",
      "Redwood Meadows Trail",
    ],
    terrain:
      "A wooded river valley with estate lots stepping down toward the Elbow. Dense mature canopy and significant lot-to-lot elevation change.",
    windNote:
      "The valley shelters the floor but accelerates wind across the upper benches — conditions vary sharply over short distances.",
    seasonalWindow:
      "Leaf-off in late autumn and early spring is when the water and the lot boundaries are actually visible through the canopy.",
    bylawNote:
      "Rocky View County rules apply; the Elbow River corridor carries additional environmental-reserve restrictions.",
    subjectMix: ["acreage-estate", "luxury-residential"],
    mobilisationNote:
      "About 25 minutes west of the Calgary base on Highway 8. Same-day capable subject to authorisation.",
    faqs: [
      {
        question: "Can you fly a drone in Elbow Valley?",
        answer:
          "Yes, with authorisation. The corridor sits within the Springbank (CYBW) control zone's area of influence, so flights are conducted under an Advanced RPAS certificate with a NAV CANADA authorisation filed before the shoot.",
      },
      {
        question: "Does tree cover affect an Elbow Valley aerial shoot?",
        answer:
          "Significantly. Mature canopy hides lot depth, river frontage and open ground — the three things an estate buyer most wants to assess. Leaf-off in late autumn or early spring gives a far more honest picture of the parcel.",
      },
      {
        question: "What altitude works best for wooded estate lots?",
        answer:
          "Just above canopy height for the reveal, rising for the plan view. Flying too high loses the sense of enclosure that makes a wooded lot desirable; staying too low hides the boundary entirely.",
      },
    ],
    primaryKeywords: [
      "drone photography Elbow Valley",
      "Elbow Valley estate aerial photography",
      "Elbow Valley drone video Alberta",
    ],
    nearestLocations: ["springbank", "bragg-creek", "aspen-woods", "bearspaw", "priddis"],
  },

  {
    slug: "bragg-creek",
    name: "Bragg Creek",
    region: "cochrane-foothills",
    city: "Bragg Creek",
    province: "Alberta",
    coordinates: { lat: 50.95, lng: -114.57 },
    tier: 2,
    driveMinutesFromCalgary: 45,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 24,
      authorizationRequired: false,
      authorizationNote:
        "Bragg Creek sits in uncontrolled Class G airspace west of the Springbank control zone. Kananaskis Country provincial land begins nearby and carries its own restrictions.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["Kananaskis Country provincial land (west and south)"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Bragg Creek sits where the foothills stop being rolling and start being forest, with Kananaskis Country provincial land beginning a short distance west.",
      consequence:
        "A shot list that drifts west can cross onto provincial park land where different rules apply, and the boundary is not marked in the sky.",
      resolution:
        "The provincial boundary is plotted alongside the airspace check, so the flown area is settled before the airframe leaves the case.",
    },
    landmarks: [
      "Elbow River",
      "White Avenue",
      "Wintergreen",
      "Balsam Avenue",
      "Highway 22",
    ],
    terrain:
      "Forested foothills on the Elbow River. Dense spruce and pine, meaningful elevation change, and the front ranges immediately west.",
    windNote:
      "Foothills valley wind, funnelled by the river corridor and unpredictable in the lee of ridges.",
    seasonalWindow:
      "Autumn larch and aspen colour is the strongest window. Winter gives clean contrast between snow and dark conifer.",
    bylawNote:
      "Rocky View County rules apply in the hamlet; Kananaskis Country provincial restrictions begin west and south.",
    subjectMix: ["acreage-estate", "tourism-hospitality", "luxury-residential"],
    mobilisationNote:
      "About 45 minutes south-west of the Calgary base on Highway 22. Day-trip scheduling; the light window closes early against the ridges.",
    faqs: [
      {
        question: "Can you fly a drone in Bragg Creek?",
        answer:
          "Yes. The hamlet sits in uncontrolled Class G airspace west of the Springbank control zone, so no NAV CANADA authorisation is normally required. Kananaskis Country provincial land begins nearby and is plotted before the flight.",
      },
      {
        question: "Can you fly into Kananaskis Country from Bragg Creek?",
        answer:
          "Not without checking the specific provincial restrictions that apply. Kananaskis is provincial park land with its own rules, and the boundary is invisible from the air, so it is plotted on the flight plan alongside the airspace check.",
      },
      {
        question: "What is the best season for Bragg Creek aerial work?",
        answer:
          "Autumn, for colour contrast between aspen and conifer, or midwinter, for the clean separation between snow and dark spruce. Midsummer flattens the forest into a single green mass from altitude.",
      },
    ],
    primaryKeywords: [
      "drone photography Bragg Creek",
      "Bragg Creek aerial photography",
      "Bragg Creek foothills drone video",
    ],
    nearestLocations: ["elbow-valley", "redwood-meadows", "priddis", "springbank", "cochrane"],
  },

  {
    slug: "ghost-lake",
    name: "Ghost Lake",
    region: "cochrane-west",
    city: "Ghost Lake",
    province: "Alberta",
    coordinates: { lat: 51.22, lng: -114.73 },
    tier: 3,
    driveMinutesFromCalgary: 55,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 29,
      authorizationRequired: false,
      authorizationNote:
        "Ghost Lake sits in open uncontrolled Class G airspace well clear of any control zone. The operational limit here is wind, not authorisation.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Ghost Lake is one of the most reliably windy bodies of water in southern Alberta — it is a destination for wind sports precisely because the reservoir funnels chinook air.",
      consequence:
        "The same wind that makes it a kiteboarding venue is what grounds an under-planned drone shoot, often on an otherwise clear day.",
      resolution:
        "Ghost Lake shoots are booked against a morning wind window rather than a calendar slot, because the afternoon is frequently unflyable.",
    },
    landmarks: [
      "Ghost Lake reservoir",
      "CottageClub",
      "Highway 1A",
      "the Ghost Dam",
      "Waiparous Creek",
    ],
    terrain:
      "A reservoir in the Bow valley with steep banks and the front ranges rising immediately west. Water dominates every composition.",
    windNote:
      "Exceptional. The reservoir channels chinook wind along its axis and is known regionally for it — this is the defining operational constraint.",
    seasonalWindow:
      "Ice-off through freeze-up for the water to read as water. Mornings are consistently calmer than afternoons.",
    bylawNote:
      "Summer village and provincial reservoir rules apply around the shoreline; launch is arranged from private property.",
    subjectMix: ["lakefront", "tourism-hospitality", "acreage-estate"],
    mobilisationNote:
      "About 55 minutes west of the Calgary base on Highway 1A. Scheduled to the morning wind window, which usually means an early departure.",
    faqs: [
      {
        question: "Is Ghost Lake difficult to fly a drone at?",
        answer:
          "It is the windiest location in the service area. The reservoir funnels chinook air along its axis, which is exactly why it is a wind-sports destination. Shoots are booked into a morning window because afternoons are frequently unflyable.",
      },
      {
        question: "Do you need authorisation to fly at Ghost Lake?",
        answer:
          "No. Ghost Lake sits in open uncontrolled Class G airspace well clear of any control zone. Nothing about the paperwork is difficult here — the constraint is entirely weather.",
      },
      {
        question: "When does Ghost Lake photograph best?",
        answer:
          "Early morning between ice-off and freeze-up. The water is calmest, the front ranges to the west are lit, and the reservoir reads as water rather than as the flat grey field it becomes under ice.",
      },
    ],
    primaryKeywords: [
      "drone photography Ghost Lake",
      "Ghost Lake aerial photography Alberta",
      "Ghost Lake reservoir drone video",
    ],
    nearestLocations: ["cochrane", "waiparous", "gleneagles", "exshaw", "morley"],
  },

  {
    slug: "gleneagles",
    name: "GlenEagles",
    region: "cochrane-foothills",
    city: "Cochrane",
    province: "Alberta",
    coordinates: { lat: 51.17, lng: -114.49 },
    tier: 3,
    driveMinutesFromCalgary: 38,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 24,
      authorizationRequired: false,
      authorizationNote:
        "GlenEagles sits on the south bench above Cochrane in uncontrolled Class G airspace. The specific address is confirmed against NAV Drone before the flight.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "GlenEagles sits on the bench above Cochrane, roughly a hundred metres higher than the town centre, wrapped around a golf course.",
      consequence:
        "The view is the product here, and a view is the single hardest thing to photograph from inside the house that has it.",
      resolution:
        "A rising pass from the fairway to above roof height puts the house, the course and the valley beyond into one continuous frame.",
    },
    landmarks: [
      "GlenEagles Golf Course",
      "Glenbow Drive",
      "GlenEagles Terrace",
      "the Bow valley escarpment",
      "Highway 1A",
    ],
    terrain:
      "An elevated bench above the Bow valley, built around a golf course, with the ground falling away sharply to the north and east.",
    windNote:
      "Elevated and exposed. The bench catches chinook wind before the town below feels it.",
    seasonalWindow:
      "Green season for the golf course context; late-day west light for the valley view. The two rarely conflict.",
    bylawNote:
      "Town of Cochrane rules apply. Golf-course launch requires the course operator's permission.",
    subjectMix: ["luxury-residential", "resort-terrain"],
    mobilisationNote:
      "About 38 minutes from the Calgary base, a few minutes past Cochrane proper. Same-day capable.",
    faqs: [
      {
        question: "Can you photograph GlenEagles golf-course homes from the air?",
        answer:
          "Yes. GlenEagles sits in uncontrolled Class G airspace, so no authorisation is normally required. Launching from the course itself needs the operator's permission, which is arranged before the shoot rather than on the day.",
      },
      {
        question: "How do you photograph a view property?",
        answer:
          "By showing the house and what it looks at in the same frame. A rising pass from below roof height puts the property, the course and the Bow valley into one continuous move, which is something no interior window shot can achieve.",
      },
      {
        question: "Is GlenEagles windier than Cochrane town centre?",
        answer:
          "Usually. GlenEagles sits about a hundred metres above the town on an exposed bench, so it catches chinook wind before the valley floor does. Conditions are checked at bench height, not from the town reading.",
      },
    ],
    primaryKeywords: [
      "drone photography GlenEagles",
      "GlenEagles Cochrane aerial photography",
      "GlenEagles golf course drone video",
    ],
    nearestLocations: ["cochrane", "bearspaw", "ghost-lake", "springbank", "calgary"],
  },

  {
    slug: "aspen-woods",
    name: "Aspen Woods",
    region: "calgary-sw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.05, lng: -114.21 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Controlled — between CYYC and CYBW",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Aspen Woods sits in west Calgary between the Calgary International control zone and Springbank. The CYBW boundary runs closer to the western communities than most operators expect, so authorisation is filed per site.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Aspen Woods sits on Calgary's western edge at one of the highest elevations in the city, with an unbroken sightline to the front ranges.",
      consequence:
        "That mountain view is priced into the real estate, and it exists for perhaps forty minutes a day — facing west, late, with the light behind the peaks.",
      resolution:
        "West-facing shoots are scheduled into that window specifically, so the view the listing claims is the view the listing shows.",
    },
    landmarks: [
      "Aspen Stone Boulevard",
      "17 Avenue SW",
      "Aspen Landing",
      "Bow Trail",
      "Old Banff Coach Road",
    ],
    terrain:
      "Elevated western upland at the city edge, sloping toward the Elbow to the south. Among the highest ground in Calgary.",
    windNote:
      "The city's most chinook-exposed quadrant. West wind arrives off the foothills before it reaches the rest of Calgary.",
    seasonalWindow:
      "Winter and early spring give the clearest mountain sightline; late-summer smoke is what most often erases it.",
    bylawNote:
      "City of Calgary park bylaws restrict launch and landing on municipal parkland. Private-property launch is the default.",
    subjectMix: ["luxury-residential", "new-build-subdivision"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. The shortest mobilisation of any location in the service area.",
    faqs: [
      {
        question: "Do you need authorisation to fly a drone in Aspen Woods?",
        answer:
          "Yes. West Calgary sits between the Calgary International and Springbank control zones, and the CYBW boundary runs closer to the western communities than most operators expect. Authorisation is filed per site before the shoot is confirmed.",
      },
      {
        question: "When is the mountain view actually visible from Aspen Woods?",
        answer:
          "Most reliably in winter and early spring, late in the day, facing west. Late-summer wildfire smoke is the single most common reason the front ranges do not appear in an Aspen Woods frame.",
      },
      {
        question: "Is Aspen Woods higher than the rest of Calgary?",
        answer:
          "It sits on some of the highest ground in the city, on the western upland. That elevation is why the mountain sightline exists here and does not from most eastern communities at the same altitude above ground.",
      },
    ],
    primaryKeywords: [
      "drone photography Aspen Woods",
      "Aspen Woods Calgary aerial photography",
      "west Calgary luxury real estate drone",
    ],
    nearestLocations: ["springbank", "elbow-valley", "calgary", "bearspaw", "bragg-creek"],
  },
];
